import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Box, Button, Grid, Paper, TextField, Snackbar, Alert } from '@mui/material';
import { Autocomplete, CheckBox, Select } from '../../components/shared';
import { Datepicker, PercentTextField, Breadcrumbs } from '../../components/shared';
import { PerformantDecimalField } from '../../components/shared/PerformantDecimalField/PerformantDecimalField';
import { RATE_TYPE_OPTIONS, RATE_TYPES } from '../../constants/RatesConstants';
import { useLazyGetUsersForSelectQuery } from '../../services/Users/UsersService';
import { useDebounce } from '../../hooks';
import { useCreateIndependentRepMutation } from '../../services/RepRate/RepRateService';
import { useGetAllSublocationsQuery } from '../../services/Sublocation/SublocationService';

import { getDateFormat } from '../../utils/common-methods';
import ConfirmationModal from '../../components/ConfirmationModel';
import { useSelector } from 'react-redux';
import { routeConstants } from '../../constants/routeConstants';
import { useNavigate } from 'react-router-dom';
import BREADCRUMB_NAMES from '../../constants/breadcrumbNames';

const AssignIndependantRep = () => {
  const navigate = useNavigate();
  const [disableSave, setDisableSave] = useState(true);
  const [rateVisible, setRateVisible] = useState(false);
  const [feeVisible, setFeeVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const updatedById = useSelector((state) => state.user?.userDetails?.id);
  const [selectedRepOption, setSelectedRepOption] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Sublocation state
  const [subLocations, setSubLocations] = useState([]);
  const [subLocationSearch, setSubLocationSearch] = useState('');

  const [formData, setFormData] = useState({
    fee: '',
    inEffectFrom: getDateFormat(new Date()),
    inEffectTo: '',
    rate: '',
    rep: '',
    type: 'None',
    note: '',
    subLocation: null, // Single sublocation object
  });

  const [userSearchText, setUserSearchText] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [fieldTouched, setFieldTouched] = useState({
    rep: false,
    subLocation: false,
    inEffectFrom: false,
  });

  const debouncedSearchText = useDebounce(userSearchText, 600);
  const debouncedSubLocationSearch = useDebounce(subLocationSearch, 300);

  const [triggerLazyQuery, { currentData: lazyIndependentRepResponse, isSuccess: lazyIndependentRepIsSuccess }] =
    useLazyGetUsersForSelectQuery();
  const [createIndependentRep] = useCreateIndependentRepMutation();

  // Query for sublocations
  const { currentData: sublocationsResponse } = useGetAllSublocationsQuery(
    {
      searchParams: `q=${debouncedSubLocationSearch}`,
      perPage: 100,
      isIr: true,
    },
    { skip: false },
  );

  // Search for users when text changes
  useEffect(() => {
    const searchParams = showAll ? `q=${debouncedSearchText}` : `q=${debouncedSearchText}&role=11`;

    if (debouncedSearchText) {
      triggerLazyQuery({
        searchParams,
        perPage: 100,
        page: 1,
      });
    }
  }, [debouncedSearchText, showAll, triggerLazyQuery]);

  // Update sublocations when search response changes
  useEffect(() => {
    setSubLocations(sublocationsResponse || []);
  }, [sublocationsResponse]);

  // Check form validity
  useEffect(() => {
    const { inEffectFrom, rep, subLocation } = formData;
    const shouldDisable = inEffectFrom === '' || rep === '' || !subLocation || !checkTypeCondition();
    setDisableSave(shouldDisable);
  }, [formData]);

  const checkTypeCondition = () => {
    const { type, rate, fee } = formData;

    switch (type) {
      case RATE_TYPES.RATE_ONLY:
        return Number(rate) > 0;
      case RATE_TYPES.FIXED_ONLY:
        return Number(fee) > 0;
      case RATE_TYPES.BOTH:
        return Number(rate) > 0 && Number(fee) > 0;
      case RATE_TYPES.None:
        return Number(rate) === 0 && Number(fee) === 0;
      default:
        return false;
    }
  };

  const independentRepOptions = useMemo(() => {
    if (lazyIndependentRepIsSuccess && lazyIndependentRepResponse?.users) {
      return lazyIndependentRepResponse.users
        .filter((user) => (user.firstName || user.lastName) && user.firstName !== '* All Users')
        .map((user) => ({
          text: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          value: user.id,
        }));
    }
    return [];
  }, [lazyIndependentRepIsSuccess, lazyIndependentRepResponse]);

  const handleRepChange = (e, value) => {
    setFieldTouched((prev) => ({ ...prev, rep: true }));
    setSelectedRepOption(value);
    setFormData((prev) => ({ ...prev, rep: value?.value || '' }));
  };

  // Handle sublocation input change
  const onSubLocationInputChange = (event, value) => {
    setSubLocationSearch(value);
  };

  // Handle sublocation selection change
  const onSubLocationChange = (event, selectedSubLocation) => {
    setFieldTouched((prev) => ({ ...prev, subLocation: true }));
    setFormData((prev) => ({ ...prev, subLocation: selectedSubLocation }));
  };

  const handleShowAllChange = (event) => {
    const isChecked = event.target.checked;
    setShowAll(isChecked);
    setUserSearchText('');
    setSelectedRepOption(null);
    setFormData((prev) => ({
      ...prev,
      rep: '',
    }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSave = (overrideFlag) => {
    setFormSubmitted(true);
    setFieldTouched({ rep: true, subLocation: true, inEffectFrom: true });

    // Check if sublocation is selected
    if (!formData.subLocation) {
      showSnackbar('Please select a sublocation', 'error');
      return;
    }

    if (!formData.inEffectFrom) {
      showSnackbar('Please select In Effect From date', 'error');
      return;
    }
    // Create record for the selected sublocation
    const sublocationId = formData.subLocation.id;

    const newRecord = {
      inEffectFrom: formData.inEffectFrom,
      inEffectTo: formData.inEffectTo || null,
      userId: formData.rep,
      userPaymentCode: formData.type || null,
      userRate: parseFloat(formData.rate || 0),
      userFee: parseFloat(formData.fee || 0),
      note: formData.note || '',
      subLocationId: sublocationId,
      updatedById: updatedById,
      override: !!overrideFlag,
    };

    createIndependentRep(newRecord)
      .unwrap()
      .then(() => {
        showSnackbar('Independent Rep assigned to sublocation successfully!');
        // Reset form after successful submission
        setFormData({
          fee: '',
          inEffectFrom: getDateFormat(new Date()),
          inEffectTo: '',
          rate: '',
          rep: '',
          type: 'None',
          note: '',
          subLocation: null,
        });
        setSelectedRepOption(null);
        setFeeVisible(false);
        setRateVisible(false);
        setFormSubmitted(false);
        setFieldTouched({ rep: false, subLocation: false, inEffectFrom: false });

        // Navigate back to listing page after a delay
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      })
      .catch((error) => {
        // Handle custom status codes
        if (error.status === 409) {
          setErrorMessage(error.data?.message || 'A conflict occurred while saving the record.');
          setShowErrorModal(true);
        } else {
          showSnackbar('Failed to assign Independent Rep', 'error');
        }
      });
  };

  // Only show errors if the field has been touched or form was submitted
  const shouldShowError = (fieldName) => fieldTouched[fieldName] || formSubmitted;
  const {
    SETTINGS: { MANAGE_INDEPENDENT_REP, SETTINGS },
  } = BREADCRUMB_NAMES;
  const {
    INDEPENDENT_REP: { ASSIGN_REP },
  } = BREADCRUMB_NAMES;
  const breadcrumbData = useMemo(
    () => [
      { text: SETTINGS, redirection: routeConstants.SETTING_ROUTE },
      {
        text: MANAGE_INDEPENDENT_REP,
        redirection: `${routeConstants.SETTING_ROUTE}/${routeConstants.INDEPENDANT_REP}`,
      },
      { text: ASSIGN_REP },
    ],
    [],
  );
  return (
    <div>
      <Box mb={2}>
        <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      </Box>
      <Typography variant="h1" mb={3}>
        Assign Independent Rep
      </Typography>

      <Box component={Paper} p={3}>
        <Grid container spacing={2} mb={3}>
          {/* Sublocation - Changed to single selection */}
          <Grid item xs={12}>
            <Autocomplete
              label="Sublocation"
              value={formData.subLocation}
              options={subLocations}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={onSubLocationChange}
              onInputChange={onSubLocationInputChange}
              filterSelectedOptions
              noOptionsText="No sublocations found"
              error={shouldShowError('subLocation') && !formData.subLocation}
              helperText={shouldShowError('subLocation') && !formData.subLocation ? 'Sublocation is required' : ''}
              onBlur={() => setFieldTouched((prev) => ({ ...prev, subLocation: true }))}
            />
          </Grid>

          {/* In Effect From */}
          <Grid item xs={12} sm={6} md={4}>
            <Datepicker
              label="In Effect From"
              name="inEffectFrom"
              id="inEffectFrom"
              inputFormat="MM/DD/YYYY"
              error={shouldShowError('inEffectFrom') && !formData.inEffectFrom}
              helperText={
                shouldShowError('inEffectFrom') && !formData.inEffectFrom ? 'In Effect From date is required' : ''
              }
              onChange={(date) => {
                setFormData((prev) => ({
                  ...prev,
                  inEffectFrom: date ? getDateFormat(date) : '',
                }));
                setFieldTouched((prev) => ({ ...prev, inEffectFrom: true }));
              }}
              value={formData?.inEffectFrom ? formData.inEffectFrom : null}
            />
          </Grid>

          {/* In Effect To */}
          <Grid item xs={12} sm={6} md={4}>
            <Datepicker
              label="In Effect To"
              name="inEffectTo"
              id="inEffectTo"
              inputFormat="MM/DD/YYYY"
              onChange={(date) =>
                setFormData((prev) => ({
                  ...prev,
                  inEffectTo: date ? getDateFormat(date) : '',
                }))
              }
              value={formData?.inEffectTo ? formData.inEffectTo : null}
              minDate={formData?.inEffectFrom ? new Date(formData.inEffectFrom) : undefined}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={3}>
          {/* Rep Name */}
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              label="Rep Name"
              options={independentRepOptions}
              value={selectedRepOption}
              getOptionLabel={(option) => option?.text || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              onInputChange={(event, newInputValue) => {
                setUserSearchText(newInputValue || ' ');
              }}
              onFocus={() => {
                if (!userSearchText) setUserSearchText(' ');
              }}
              onChange={handleRepChange}
              error={shouldShowError('rep') && !formData.rep}
              helperText={shouldShowError('rep') && !formData.rep ? 'Rep is required' : ''}
              onBlur={() => setFieldTouched((prev) => ({ ...prev, rep: true }))}
            />
          </Grid>

          {/* Show All */}
          <Grid item xs={12} sm={6} md={4} display="flex" alignItems="center">
            <CheckBox value={showAll} label="Show All Users" onChange={handleShowAllChange} />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={3}>
          {/* Type */}
          <Grid item xs={12} sm={6} md={4}>
            <Select
              inputLabel="Type"
              name="type"
              labelId="type-label"
              onChange={(e) => {
                if (e?.target?.value === RATE_TYPES.RATE_ONLY) {
                  setRateVisible(true);
                  setFeeVisible(false);
                } else if (e?.target?.value === RATE_TYPES.FIXED_ONLY) {
                  setFeeVisible(true);
                  setRateVisible(false);
                } else if (e?.target?.value === RATE_TYPES.BOTH) {
                  setRateVisible(true);
                  setFeeVisible(true);
                } else {
                  setRateVisible(false);
                  setFeeVisible(false);
                }
                setFormData((prev) => ({
                  ...prev,
                  type: e?.target?.value,
                  rate: 0,
                  fee: 0,
                }));
              }}
              value={formData?.type}
              options={RATE_TYPE_OPTIONS}
            />
          </Grid>

          {/* Rate */}
          {rateVisible && (
            <Grid item xs={12} sm={6} md={4}>
              <PercentTextField
                label="Rate"
                name="rate"
                value={formData?.rate || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, rate: e?.value }))}
                error={
                  ((formData.type === RATE_TYPES.RATE_ONLY || formData.type === RATE_TYPES.BOTH) &&
                    (!formData.rate || formData.rate <= 0)) ||
                  (formData.type === RATE_TYPES.None && Number(formData.rate) > 0)
                }
                helperText={
                  (formData.type === RATE_TYPES.RATE_ONLY || formData.type === RATE_TYPES.BOTH) &&
                  (!formData.rate || formData.rate <= 0)
                    ? 'Rate must be greater than 0'
                    : formData.type === RATE_TYPES.None && Number(formData.rate) > 0
                    ? 'Rate must be 0'
                    : ''
                }
                disabled={formData.type === RATE_TYPES.None}
              />
            </Grid>
          )}

          {/* Fee */}
          {feeVisible && (
            <Grid item xs={12} sm={6} md={4}>
              <PerformantDecimalField
                useFormik={false}
                label="Fee"
                name="fee"
                value={formData?.fee || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }))}
                error={
                  ((formData.type === RATE_TYPES.FIXED_ONLY || formData.type === RATE_TYPES.BOTH) &&
                    (!formData.fee || formData.fee <= 0)) ||
                  (formData.type === RATE_TYPES.None && Number(formData.fee) > 0)
                }
                helperText={
                  (formData.type === RATE_TYPES.FIXED_ONLY || formData.type === RATE_TYPES.BOTH) &&
                  (!formData.fee || formData.fee <= 0)
                    ? 'Fee must be greater than 0'
                    : formData.type === RATE_TYPES.None && Number(formData.fee) > 0
                    ? 'Fee must be 0'
                    : ''
                }
                disabled={formData.type === RATE_TYPES.None}
              />
            </Grid>
          )}
        </Grid>

        {/* Note */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Note"
              variant="outlined"
              placeholder="Write a note..."
              name="note"
              value={formData?.note || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }))}
            />
          </Grid>
        </Grid>

        {/* Buttons */}
        <Box display="flex" justifyContent="flex-start" gap={2} mt={3}>
          <Button variant="contained" color="primary" disabled={disableSave} onClick={() => handleSave(false)}>
            Assign
          </Button>
        </Box>
      </Box>

      {/* Confirmation Modal for Date Conflicts */}
      <ConfirmationModal
        isOpen={showErrorModal}
        title="Date Conflict"
        msg={errorMessage}
        buttons={[
          { text: 'Confirm', value: 'confirm' },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          setShowErrorModal(false);
          if (val === 'confirm') {
            handleSave(true);
          }
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AssignIndependantRep;
